// workoutSessions.js — THE workout count for a "workouts a week" goal
// (2026-10-27). Counts SESSIONS, not distinct days: Muay Thai at noon and a
// lift in the evening are two workouts. Before this file every resolver
// collapsed a day to one (a Set of date strings), so two sessions logged on
// one day read 1/4 on the goal card, in Kai's context and in the Health score.
//
// The only dedupe left is the SAME session recorded twice: a HealthKit row
// (source 'healthkit', written by services/healthSync.js) and a manual log on
// the same day with the same type and an overlapping time. A manual row has
// no clock time, so time cannot disprove the overlap and the pair collapses
// to one; two HealthKit rows with disjoint times stay two.
//
// A Gym habit tick (habits.name 'gym') on a day with NO workouts row is one
// session, exactly the ledger's rule (api/lib/ledger.js): ticking the box for
// a session already logged is the same workout recorded twice.
//
// Consumers (change all or none — the resolver parity rule):
//   - src/Scorify.jsx getGoalAutoValue (the goal card)
//   - api/lib/goal-progress.js resolveProgress (Kai, the cron)
//   - src/lib/goalAdherence.js weeklyCountAdherence (the Health pillar score,
//     client and api/lib/pillars-server.js)
// Pure ESM, no deps; imported by both src/ and api/.

const dayOf = (d) => (typeof d === 'string' && d.length >= 10 ? d.slice(0, 10) : null);

// HealthKit types (healthSync normalizeType) and the manual picker
// (workoutLog.js WORKOUT_TYPES) name the same activity differently.
const TYPE_ALIASES = {
  weighttraining: 'lift', lift: 'lift', strength: 'lift', weights: 'lift',
  ride: 'cycle', cycle: 'cycle', bike: 'cycle', cycling: 'cycle',
  hike: 'walk', walk: 'walk', walking: 'walk', hiking: 'walk',
  run: 'run', running: 'run',
  swim: 'swim', swimming: 'swim',
  yoga: 'yoga',
  hiit: 'hiit',
};
const GENERIC = new Set(['workout', 'other', '']);

/** Canonical type key, or '' for a generic type (Apple's "Workout", the picker's "Other"). */
export function canonicalWorkoutType(type) {
  const t = String(type || '').trim().toLowerCase();
  if (GENERIC.has(t)) return '';
  return TYPE_ALIASES[t] || t;
}

const ms = (iso) => { const n = Date.parse(iso); return Number.isFinite(n) ? n : null; };

/** [startMs, endMs] for a row with a clock time, else null (date-only rows). */
function interval(w) {
  const start = ms(w?.start_at);
  if (start == null) return null;
  let end = ms(w?.end_at);
  if (end == null) {
    const dur = Number(w?.duration_min);
    end = Number.isFinite(dur) && dur > 0 ? start + dur * 60000 : start;
  }
  return [start, Math.max(start, end)];
}

/**
 * The same session recorded twice: one HealthKit row and one non HealthKit
 * row, same local date, same (or generic) type, overlapping time. A row with
 * no clock time cannot disprove the overlap.
 */
export function isSameSession(a, b) {
  if (!a || !b) return false;
  const aHk = a.source === 'healthkit', bHk = b.source === 'healthkit';
  if (aHk === bHk) return false;
  if (dayOf(String(a.date || '')) !== dayOf(String(b.date || ''))) return false;
  const ta = canonicalWorkoutType(a.type), tb = canonicalWorkoutType(b.type);
  if (ta && tb && ta !== tb) return false;
  const ia = interval(a), ib = interval(b);
  if (!ia || !ib) return true;
  return ia[0] <= ib[1] && ib[0] <= ia[1];
}

/** Workouts rows with a HealthKit/manual duplicate collapsed to the HealthKit row. Each HealthKit row absorbs at most one manual row. */
export function dedupeSessions(workouts = []) {
  const rows = (workouts || []).filter(Boolean);
  const hk = rows.filter((w) => w.source === 'healthkit');
  const absorbed = new Set();
  const out = [];
  for (const w of rows) {
    if (w.source === 'healthkit') { out.push(w); continue; }
    const twin = hk.find((h) => !absorbed.has(h) && isSameSession(h, w));
    if (twin) { absorbed.add(twin); continue; }
    out.push(w);
  }
  return out;
}

/**
 * Sessions in [start, end] (inclusive 'YYYY-MM-DD' bounds): deduped workouts
 * rows, plus one per Gym habit day with no row. Returns { count, logged,
 * fromHabits }; `logged` is the rows alone so a label can say when the
 * habit added to it.
 */
export function countWorkoutSessions({ workouts = [], gymHistory = [], start, end } = {}) {
  const inWin = (d) => !!d && (!start || d >= start) && (!end || d <= end);
  const rows = (workouts || []).filter((w) => inWin(dayOf(String(w?.date || ''))));
  const sessions = dedupeSessions(rows);
  const logged = sessions.length;
  const rowDays = new Set(sessions.map((w) => dayOf(String(w.date))));
  let fromHabits = 0;
  const habitDays = new Set();
  for (const h of gymHistory || []) {
    const d = dayOf(String(h || ''));
    if (!inWin(d) || rowDays.has(d) || habitDays.has(d)) continue;
    habitDays.add(d);
    fromHabits++;
  }
  return { count: logged + fromHabits, logged, fromHabits };
}

/** A habit's completion dates as an array of strings, whatever shape the row carries (client `completions`, server `history` JSON). */
export function gymHistoryOf(habit) {
  if (!habit) return [];
  let hist = habit.completions ?? habit.history;
  if (typeof hist === 'string') { try { hist = JSON.parse(hist); } catch { hist = []; } }
  return Array.isArray(hist) ? hist : [];
}
